import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { safeArray } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import { correspondenceCategories } from '../../data/mockData';
import {
  displayCorrespondenceCategory,
  getCategoryFieldConfig,
  isBankGuaranteeCategory,
  matchesRaBillCategory,
  matchesClearanceCategory,
  matchesMilestoneEotCategory,
  matchesDeviationsExtraItemsCategory,
  matchesScopeAgreementCategory,
  matchesQualityCategory,
} from '../../data/categoryFieldConfig';
import { getChainEntries, getChainAttachments } from '../../utils/correspondencePrint';
import ThreadReportPreviewModal from '../../components/ThreadReportPreviewModal';
import { toStoredAttachments, partitionFilesByMaxSize, correspondenceOversizeMessage } from '../../utils/attachmentStorage';
import { correspondenceMaxSizeLabel } from '../../config/attachmentLimits';
import { formatDateIN, getTodayISO } from '../../utils/dateFormat';
import { openPrintWindow, tableHtmlFromRows } from '../../utils/exportUtils';
import { downloadAttachmentsZip } from '../../utils/attachmentsZip';
import { buildInitialRegisterState, saveRegisterUiSnapshot, getRegisterFilterDefaults, resolveRegisterApiFilters } from '../../utils/correspondenceRegisterPersistence';
import {
  correspondenceAPI,
  raBillsAPI,
  bankGuaranteesAPI,
  deviationsAPI,
  milestonesAPI,
  qaqcAPI,
  clearancesAPI,
  adminAPI,
} from '../../services/authService';
import { notifySuccess, notifyError } from '../../utils/notify';
import { confirmDialog } from '../../utils/confirmDialog';
import { DEC_INFRA } from '../../config/brandingConfig';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import TablePagination from '../../components/TablePagination';
import CorrespondenceSubjectTooltip from '../../components/CorrespondenceSubjectTooltip';
import StatusBadge from '../../components/StatusBadge';
import CorrespondenceViewsToolbar from './CorrespondenceViewsToolbar';
import { IconStar, IconPin } from './CorrespondenceViewIcons';
import './correspondenceViews.css';
import { createPermissionChecker } from '../../utils/rbacPermissions';
import { useFormConfiguration } from '../../hooks/useFormConfiguration';
import { ConfiguredField } from '../../components/ConfiguredField';
import { DynamicFieldsSection } from '../../components/DynamicFieldRenderer';

const PRIORITY_CLASS_MAP = {
  Immediate: 'high',
  Urgent: 'medium',
  Routine: 'low',
  Critical: 'critical',
  High: 'high',
  Medium: 'medium',
  Low: 'low',
};

const CLIENT_SEARCH_PAGE_SIZE = 500;
const CLIENT_SEARCH_MAX_PAGES = 40;

/** Match correspondence subject or letter reference (partial, case-insensitive). */
const LOCATION_BLOCK_UNSET = '__unset__';

const chainMatchesLocation = (chain, exactFilter, searchTerm) => {
  const loc = String(chain?.location_block || '').trim();
  const exact = String(exactFilter || '').trim();
  const search = String(searchTerm || '').trim();
  if (exact && exact !== 'all') {
    if (exact === LOCATION_BLOCK_UNSET) {
      if (loc) return false;
    } else if (loc !== exact) {
      return false;
    }
  }
  if (search && !loc.toLowerCase().includes(search.toLowerCase())) {
    return false;
  }
  return true;
};

const chainMatchesSearch = (chain, term) => {
  const q = String(term || '').trim().toLowerCase();
  if (!q) return true;
  if (String(chain?.subject || '').toLowerCase().includes(q)) return true;
  const refs = [chain?.letter_refs, chain?.first_letter_ref].filter(Boolean).join(' ').toLowerCase();
  return refs.includes(q);
};

/** Production MySQL backends reject Prisma `mode: 'insensitive'` and return 500 on search. */
const isServerSearchError = (error) => {
  const status = error?.response?.status;
  if (status === 500 || status === 400) return true;
  const msg = String(error?.response?.data?.message || error?.message || '');
  return /Unknown argument `mode`|prisma\.correspondenceChain/i.test(msg);
};

const CorrespondenceRegister = ({ filter, onViewThread, onNavigateRegisterFilter }) => {
  const [threadReportPreview, setThreadReportPreview] = useState(null);
  const apiCacheRef = useRef(new Map());
  const serverSearchBrokenRef = useRef(false);
  const skipAutoPageResetRef = useRef(true);
  const getToday = getTodayISO;
  const normalizeMode = (value) => {
    if (value === 'Email') return 'Email';
    return 'Letter';
  };
  const isEmailCorrespondenceMode = (mode) => normalizeMode(mode) === 'Email';
  const initialUi = useMemo(() => buildInitialRegisterState(filter), [filter]);
  const [searchQuery, setSearchQuery] = useState(initialUi.searchQuery);
  const [searchParam, setSearchParam] = useState(initialUi.searchParam);
  const [projectFilter, setProjectFilter] = useState(initialUi.projectFilter);
  const [dateFrom, setDateFrom] = useState(initialUi.dateFrom);
  const [dateTo, setDateTo] = useState(initialUi.dateTo);
  const [debouncedDateFrom, setDebouncedDateFrom] = useState(initialUi.debouncedDateFrom);
  const [debouncedDateTo, setDebouncedDateTo] = useState(initialUi.debouncedDateTo);
  const [dateRangePreset, setDateRangePreset] = useState(initialUi.dateRangePreset);
  const [typeFilter, setTypeFilter] = useState(initialUi.typeFilter);
  const [categoryFilter, setCategoryFilter] = useState(initialUi.categoryFilter);
  const [locationBlockFilter, setLocationBlockFilter] = useState(initialUi.locationBlockFilter);
  const [locationBlockSearch, setLocationBlockSearch] = useState(initialUi.locationBlockSearch);
  const [debouncedLocationBlockSearch, setDebouncedLocationBlockSearch] = useState(initialUi.debouncedLocationBlockSearch);
  const [locationBlockOptions, setLocationBlockOptions] = useState([]);
  const [statusFilter, setStatusFilter] = useState(initialUi.statusFilter);
  const [priorityFilter, setPriorityFilter] = useState(initialUi.priorityFilter);
  const [page, setPage] = useState(initialUi.page);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchMatches, setSearchMatches] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [downloadingAttachments, setDownloadingAttachments] = useState(false);
  const [apiChains, setApiChains] = useState([]);
  const [apiEntries, setApiEntries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [useApiData, setUseApiData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [projectSummaryRows, setProjectSummaryRows] = useState([]);
  const [projectSummaryTotals, setProjectSummaryTotals] = useState({ inwardLetters: 0, outwardLetters: 0, totalLetters: 0 });
  const [projectSummaryAsOn, setProjectSummaryAsOn] = useState('');
  const [projectSummaryLoading, setProjectSummaryLoading] = useState(false);
  const [showProjectSummary, setShowProjectSummary] = useState(false);
  const [registerQuickFilter, setRegisterQuickFilter] = useState(initialUi.registerQuickFilter || 'all');
  const [savedViewId, setSavedViewId] = useState(initialUi.savedViewId || null);
  const { hasPermission } = useMemo(() => createPermissionChecker(), []);
  const canManagePersonalFlags = hasPermission('correspondence', 'manage_personal_flags') || hasPermission('correspondence', 'update');
  const canManageSavedViews = hasPermission('correspondence', 'manage_saved_views') || hasPermission('correspondence', 'update');
  const { projects: availableProjects } = useProjects();

  const [newChain, setNewChain] = useState({
    project_id: '', subject: '', type: 'Query', category: 'General', location_block: '', severity: 'Routine',
    direction: 'Outward', letter_date: getToday(), letter_ref_no: '', from: DEC_INFRA, to: '', summary: '', sla_days: 7,
    response_expected: true, mode: 'Letter', category_data: {}, attachments: []
  });
  const formConfig = useFormConfiguration({ pageKey: 'correspondence_register', formKey: 'create_subject', mode: 'create' });
  const [customValues, setCustomValues] = useState({});
  const defaultsFromCustomFields = useCallback(() => {
    const out = {};
    for (const f of formConfig.customFields || []) {
      if (f.defaultValue != null && f.defaultValue !== '') out[f.fieldKey] = f.defaultValue;
      else if (f.fieldType === 'boolean') out[f.fieldKey] = false;
      else out[f.fieldKey] = '';
    }
    return out;
  }, [formConfig.customFields]);
  const onCustomChange = (fieldKey, value) => {
    setCustomValues((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const selectedProject = useMemo(
    () => (newChain.project_id ? availableProjects.find((p) => String(p.id) === String(newChain.project_id)) : null),
    [availableProjects, newChain.project_id]
  );
  const briefDesignations = useMemo(
    () => (selectedProject?.brief_designations || []).filter((d) => d && String(d).trim()),
    [selectedProject]
  );
  const perPage = 50;

  const getPriorityClass = (value) => PRIORITY_CLASS_MAP[value] || 'medium';

  const normalizeApiChain = (chain) => ({
    ...chain,
    type: chain.type || 'Query',
    severity: chain.severity || 'Routine',
    entry_count: chain.entry_count ?? 0,
    last_entry_date: chain.last_entry_date || chain.created_at || '',
    created_at: chain.created_at || '',
    isImportantForMe: chain.isImportantForMe ?? chain.userFlags?.isImportant ?? false,
    isPinnedForMe: chain.isPinnedForMe ?? chain.userFlags?.isPinned ?? false,
  });

  const buildListFilterParams = useCallback(() => {
    const { apiStatus, apiPriority } = resolveRegisterApiFilters(filter, statusFilter, priorityFilter, registerQuickFilter);
    return {
      ...(projectFilter !== 'all' && { projectId: projectFilter }),
      ...(apiStatus !== 'all' && apiStatus && { status: apiStatus }),
      ...(categoryFilter !== 'all' && { category: categoryFilter }),
      ...(locationBlockFilter !== 'all' && { locationBlock: locationBlockFilter }),
      ...(debouncedLocationBlockSearch && { locationBlockSearch: debouncedLocationBlockSearch }),
      ...(apiPriority !== 'all' && apiPriority && { priority: apiPriority }),
      ...(debouncedDateFrom && { dateFrom: debouncedDateFrom }),
      ...(debouncedDateTo && { dateTo: debouncedDateTo }),
      ...(registerQuickFilter === 'important' && { importantOnly: true }),
      ...(registerQuickFilter === 'pinned' && { pinnedOnly: true }),
      ...(registerQuickFilter === 'overdue' && { overdueOnly: true }),
      ...(registerQuickFilter === 'critical' && filter === 'all' && { criticalOnly: true }),
      ...(savedViewId && { savedViewId }),
    };
  }, [filter, statusFilter, priorityFilter, projectFilter, categoryFilter, locationBlockFilter, debouncedLocationBlockSearch, debouncedDateFrom, debouncedDateTo, registerQuickFilter, savedViewId]);

  const fetchAllChainsForFilters = useCallback(async (filterParams) => {
    const all = [];
    let total = 0;
    let pageNum = 1;
    do {
      const resp = await correspondenceAPI.getChains({
        ...filterParams,
        page: pageNum,
        pageSize: CLIENT_SEARCH_PAGE_SIZE,
      });
      const data = resp?.data || {};
      const chains = (data?.chains || []).map(normalizeApiChain);
      total = data?.totalCount ?? chains.length;
      all.push(...chains);
      pageNum += 1;
    } while (all.length < total && pageNum <= CLIENT_SEARCH_MAX_PAGES);
    return all;
  }, []);

  const loadFromApi = useCallback(async () => {
    setIsLoadingData(true);
    try {
      const filterParams = buildListFilterParams();
      const term = String(searchParam || '').trim();
      const useClientSearch = Boolean(term) && serverSearchBrokenRef.current;

      const applyListResult = (chains, count, cacheKey, data) => {
        setApiChains(chains);
        setApiEntries([]);
        setTotalCount(count);
        setUseApiData(true);
        if (cacheKey) {
          apiCacheRef.current.set(cacheKey, { ts: Date.now(), data: data || { chains, totalCount: count } });
        }
      };

      const readCache = (cacheKey) => {
        const cached = apiCacheRef.current.get(cacheKey);
        if (cached && (Date.now() - cached.ts) < 60_000) {
          const data = cached.data || {};
          const chains = (data?.chains || []).map(normalizeApiChain);
          applyListResult(chains, data?.totalCount ?? chains.length, null);
          return true;
        }
        return false;
      };

      if (term && useClientSearch) {
        const cacheKey = JSON.stringify({ mode: 'client', term, page, ...filterParams });
        if (readCache(cacheKey)) return;
        const all = await fetchAllChainsForFilters(filterParams);
        const filtered = all.filter((c) => chainMatchesSearch(c, term) && chainMatchesLocation(c, locationBlockFilter, debouncedLocationBlockSearch));
        const start = (page - 1) * perPage;
        const pageRows = filtered.slice(start, start + perPage);
        applyListResult(pageRows, filtered.length, cacheKey, { chains: pageRows, totalCount: filtered.length });
        return;
      }

      const params = { ...filterParams, page, pageSize: perPage, ...(term && { search: term }) };
      const cacheKey = JSON.stringify({ mode: 'server', ...params });
      if (readCache(cacheKey)) return;

      try {
        const resp = await correspondenceAPI.getChains(params);
        const data = resp?.data;
        const chains = (data?.chains || []).map(normalizeApiChain);
        applyListResult(chains, data?.totalCount ?? chains.length, cacheKey, data);
      } catch (error) {
        if (!term || !isServerSearchError(error)) throw error;
        serverSearchBrokenRef.current = true;
        apiCacheRef.current.clear();
        const all = await fetchAllChainsForFilters(filterParams);
        const filtered = all.filter((c) => chainMatchesSearch(c, term) && chainMatchesLocation(c, locationBlockFilter, debouncedLocationBlockSearch));
        const start = (page - 1) * perPage;
        const pageRows = filtered.slice(start, start + perPage);
        const clientCacheKey = JSON.stringify({ mode: 'client', term, page, ...filterParams });
        applyListResult(pageRows, filtered.length, clientCacheKey, { chains: pageRows, totalCount: filtered.length });
      }
    } catch (error) {
      console.warn('Correspondence API not reachable:', error?.message || error);
      setUseApiData(false);
      setApiChains([]);
      setApiEntries([]);
      setTotalCount(0);
    } finally {
      setIsLoadingData(false);
    }
  }, [filter, statusFilter, priorityFilter, projectFilter, categoryFilter, locationBlockFilter, debouncedLocationBlockSearch, debouncedDateFrom, debouncedDateTo, searchParam, page, buildListFilterParams, fetchAllChainsForFilters, perPage, registerQuickFilter, savedViewId]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedDateFrom(dateFrom);
      setDebouncedDateTo(dateTo);
    }, 500);
    return () => clearTimeout(t);
  }, [dateFrom, dateTo]);

  const applyRegisterSearch = useCallback(() => {
    const trimmed = searchQuery.trim();
    setSearchParam(trimmed);
    setPage(1);
    apiCacheRef.current.clear();
  }, [searchQuery]);

  const clearRegisterSearch = useCallback(() => {
    setSearchQuery('');
    setSearchParam('');
    setPage(1);
    apiCacheRef.current.clear();
  }, []);

  /**
   * Reset to page 1 when list criteria change — skip first run so restored session snapshot keeps its page.
   * Use dateFrom/dateTo (not debounced) so the post-mount date debounce timer does not retrigger this
   * and wipe a restored page / effective filter state.
   */
  useEffect(() => {
    if (skipAutoPageResetRef.current) {
      skipAutoPageResetRef.current = false;
      return;
    }
    setPage(1);
  }, [filter, statusFilter, priorityFilter, projectFilter, categoryFilter, locationBlockFilter, locationBlockSearch, dateFrom, dateTo, searchParam, registerQuickFilter, savedViewId]);

  useEffect(() => {
    saveRegisterUiSnapshot(filter, {
      searchQuery,
      searchParam,
      projectFilter,
      typeFilter,
      categoryFilter,
      locationBlockFilter,
      locationBlockSearch,
      statusFilter,
      priorityFilter,
      registerQuickFilter,
      savedViewId,
      page,
    });
  }, [
    filter,
    searchQuery,
    searchParam,
    projectFilter,
    typeFilter,
    categoryFilter,
    locationBlockFilter,
    locationBlockSearch,
    statusFilter,
    priorityFilter,
    registerQuickFilter,
    savedViewId,
    page,
  ]);

  /** Date filters always start clear when opening or switching register views. */
  useEffect(() => {
    setDateFrom('');
    setDateTo('');
    setDebouncedDateFrom('');
    setDebouncedDateTo('');
    setDateRangePreset('');
  }, [filter]);

  useEffect(() => {
    loadFromApi();
  }, [loadFromApi]);

  const loadProjectSummary = useCallback(async () => {
    setProjectSummaryLoading(true);
    try {
      const params = projectFilter !== 'all' ? { projectId: projectFilter } : {};
      const resp = await correspondenceAPI.getSummary(params);
      const data = resp?.data || {};
      setProjectSummaryRows(safeArray(data.projectSummary));
      setProjectSummaryTotals(data.projectSummaryTotals || { inwardLetters: 0, outwardLetters: 0, totalLetters: 0 });
      setProjectSummaryAsOn(String(data.asOn || '').slice(0, 10));
    } catch (_err) {
      setProjectSummaryRows([]);
      setProjectSummaryTotals({ inwardLetters: 0, outwardLetters: 0, totalLetters: 0 });
      setProjectSummaryAsOn('');
    } finally {
      setProjectSummaryLoading(false);
    }
  }, [projectFilter]);

  useEffect(() => {
    loadProjectSummary();
  }, [loadProjectSummary]);

  const chainsSource = useMemo(() => safeArray(useApiData ? apiChains : null), [useApiData, apiChains]);
  const entriesSource = useMemo(() => safeArray(useApiData ? apiEntries : null), [useApiData, apiEntries]);
  const projectOptions = safeArray(availableProjects);

  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const paginatedChains = chainsSource;
  const chainStatsById = useMemo(() => {
    const stats = {};
    safeArray(entriesSource).forEach((entry) => {
      if (!entry) return;
      const chainId = entry.chain_id;
      if (!stats[chainId]) stats[chainId] = { count: 0, lastDate: '', firstLetterRef: '', firstLetterRefDate: null, latestLetterDate: null, latestLetterDirection: null };
      stats[chainId].count += 1;
      const letterDate = String(entry.letter_date || '');
      const anyDate = String(entry.letter_date || entry.entry_date || '');
      if (anyDate && (!stats[chainId].lastDate || anyDate > stats[chainId].lastDate)) {
        stats[chainId].lastDate = anyDate;
        stats[chainId].latestLetterDirection = entry.direction || null;
      }
      if (letterDate && (!stats[chainId].firstLetterRefDate || letterDate < String(stats[chainId].firstLetterRefDate || 'zzzz'))) {
        stats[chainId].firstLetterRef = entry.ref_no || entry.letter_ref_no || '';
        stats[chainId].firstLetterRefDate = letterDate;
      }
      if (letterDate && (!stats[chainId].latestLetterDate || letterDate > String(stats[chainId].latestLetterDate || ''))) {
        stats[chainId].latestLetterDate = letterDate;
      }
    });
    return stats;
  }, [entriesSource]);

  const getChainPrefix = (chain, stats) => chain.first_letter_ref || stats?.firstLetterRef || null;


  const handleToggleImportant = async (e, chainId) => {
    e.stopPropagation();
    try {
      const res = await correspondenceAPI.toggleImportant(chainId);
      const data = res.data || {};
      setApiChains((rows) => rows.map((c) => (
        c.id === chainId
          ? { ...c, isImportantForMe: data.isImportant, isPinnedForMe: data.isPinned ?? c.isPinnedForMe }
          : c
      )));
    } catch (err) {
      notifyError(err.response?.data?.message || 'Unable to update important flag.');
    }
  };

  const handleTogglePin = async (e, chainId) => {
    e.stopPropagation();
    try {
      const res = await correspondenceAPI.togglePin(chainId);
      const data = res.data || {};
      setApiChains((rows) => rows.map((c) => (
        c.id === chainId
          ? { ...c, isImportantForMe: data.isImportant ?? c.isImportantForMe, isPinnedForMe: data.isPinned }
          : c
      )));
    } catch (err) {
      notifyError(err.response?.data?.message || 'Unable to update pin flag.');
    }
  };

  const getCurrentViewSnapshot = useCallback(() => ({
    filters: {
      projectId: projectFilter !== 'all' ? projectFilter : null,
      status: statusFilter,
      priority: priorityFilter,
      category: categoryFilter !== 'all' ? categoryFilter : null,
      locationBlock: locationBlockFilter !== 'all' ? locationBlockFilter : null,
      fromDate: debouncedDateFrom || null,
      toDate: debouncedDateTo || null,
      importantOnly: registerQuickFilter === 'important',
      pinnedOnly: registerQuickFilter === 'pinned',
      overdueOnly: registerQuickFilter === 'overdue',
      criticalOnly: registerQuickFilter === 'critical',
    },
    columns: null,
    sortBy: { field: 'lastEntryDate', direction: 'desc' },
    searchText: searchParam || '',
    pageSize: perPage,
  }), [projectFilter, statusFilter, priorityFilter, categoryFilter, locationBlockFilter, debouncedDateFrom, debouncedDateTo, registerQuickFilter, searchParam, perPage]);

  const applySavedViewToRegister = useCallback((view) => {
    const f = view?.filters || {};
    if (f.projectId) setProjectFilter(String(f.projectId));
    else if (f.projectId === null) setProjectFilter('all');
    if (f.status) setStatusFilter(f.status);
    if (f.priority) setPriorityFilter(f.priority);
    if (f.category) setCategoryFilter(f.category);
    if (f.locationBlock) setLocationBlockFilter(f.locationBlock);
    if (f.fromDate) setDateFrom(f.fromDate);
    if (f.toDate) setDateTo(f.toDate);
    if (f.importantOnly) setRegisterQuickFilter('important');
    else if (f.pinnedOnly) setRegisterQuickFilter('pinned');
    else if (f.overdueOnly) setRegisterQuickFilter('overdue');
    else if (f.criticalOnly) setRegisterQuickFilter('critical');
    else setRegisterQuickFilter('all');
    if (view?.searchText != null) {
      setSearchQuery(view.searchText);
      setSearchParam(view.searchText);
    }
    setPage(1);
    apiCacheRef.current.clear();
  }, []);

  const resetRegisterFilters = useCallback(() => {
    const d = getRegisterFilterDefaults(filter);
    setSearchQuery(d.searchQuery);
    setSearchParam(d.searchParam);
    setProjectFilter(d.projectFilter);
    setDateFrom(d.dateFrom);
    setDateTo(d.dateTo);
    setDebouncedDateFrom(d.debouncedDateFrom);
    setDebouncedDateTo(d.debouncedDateTo);
    setDateRangePreset(d.dateRangePreset);
    setTypeFilter(d.typeFilter);
    setCategoryFilter(d.categoryFilter);
    setLocationBlockFilter(d.locationBlockFilter);
    setLocationBlockSearch(d.locationBlockSearch);
    setDebouncedLocationBlockSearch(d.debouncedLocationBlockSearch);
    setStatusFilter(d.statusFilter);
    setPriorityFilter(d.priorityFilter);
    setRegisterQuickFilter('all');
    setSavedViewId(null);
    setPage(d.page);
    setShowSearchResults(false);
    setSearchMatches([]);
    apiCacheRef.current.clear();
  }, [filter]);

  const handleSubjectSearch = () => {
    if (!newChain.subject.trim()) return;
    const normalizedQuery = newChain.subject.toLowerCase().trim().replace(/\s+/g, ' ');
    const exactMatch = chainsSource.find(c => (c.subject_key || c.subject?.toLowerCase().trim().replace(/\s+/g, ' ')) === normalizedQuery);
    if (exactMatch) {
      onViewThread(exactMatch.id);
      setShowCreateModal(false);
      return;
    }
    const fuzzy = chainsSource
      .filter(c => {
        const pid = newChain.project_id ? parseInt(newChain.project_id) : null;
        const subjectKey = c.subject_key || String(c.subject || '').toLowerCase().trim().replace(/\s+/g, ' ');
        return (!pid || c.project_id === pid) && subjectKey.includes(normalizedQuery.split(' ')[0]);
      })
      .slice(0, 5);
    setSearchMatches(fuzzy);
    setShowSearchResults(true);
  };

  const titleMap = {
    all: 'Correspondence Register',
    open: 'Open / Targeted Reply Overdue',
    critical: 'Critical Correspondence',
    closed: 'Closed Correspondence',
  };

  const handleCategoryChange = (category) => {
    setNewChain({ ...newChain, category, category_data: {} });
    setFormErrors({});
  };

  const handleCategoryFieldChange = (field, value, type) => {
    const parsedValue = type === 'number' ? (value === '' ? '' : Number(value)) : value;
    setNewChain({
      ...newChain,
      category_data: {
        ...(newChain.category_data || {}),
        [field]: parsedValue
      }
    });
  };

  const handleCreateAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const { accepted, rejected } = partitionFilesByMaxSize(files);
    if (rejected.length) {
      alert(correspondenceOversizeMessage(rejected));
    }
    if (!accepted.length) {
      event.target.value = '';
      return;
    }
    setAttachmentUploading(true);
    try {
      const newFiles = await toStoredAttachments(accepted);
      setNewChain((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...newFiles] }));
    } finally {
      setAttachmentUploading(false);
    }
    event.target.value = '';
  };

  const handleRemoveCreateAttachment = (index) => {
    const next = [...(newChain.attachments || [])];
    next.splice(index, 1);
    setNewChain({ ...newChain, attachments: next });
  };

  const validateCreateChain = () => {
    const errors = {};
    if (!newChain.project_id) errors.project_id = 'Project is required.';
    if (!newChain.subject.trim()) errors.subject = 'Subject is required.';
    if (!newChain.from.trim()) errors.from = 'From is required.';
    if (!newChain.to.trim()) errors.to = 'To is required.';
    if (!newChain.letter_date) errors.letter_date = 'Letter date is required.';
    if (!isEmailCorrespondenceMode(newChain.mode) && !newChain.letter_ref_no?.trim()) errors.letter_ref_no = 'Letter reference number is required.';
    if (!newChain.attachments || newChain.attachments.length === 0) errors.attachments = 'At least one attachment is required.';
    if (newChain.sla_days < 0) errors.sla_days = 'Targeted reply days cannot be negative.';

    const categoryFields = getCategoryFieldConfig(newChain.category);
    const catData = newChain.category_data || {};
    categoryFields.forEach((field) => {
      const value = catData[field.key];
      const isEmpty = value === undefined || value === null || value === '';
      if (field.required && isEmpty) {
        errors[field.key] = `${field.label} is required.`;
      }
      if (field.type === 'number' && value !== '' && value !== undefined && Number(value) < 0) {
        errors[field.key] = `${field.label} cannot be negative.`;
      }
    });

    if (matchesRaBillCategory(newChain.category)) {
      const gross = Number(catData.gross_work_done || 0);
      const deductions = Number(catData.deductions_total || 0);
      const net = Number(catData.net_payable || 0);
      const paid = Number(catData.paid_value || 0);
      if (deductions > gross) errors.deductions_total = 'Deductions cannot exceed gross value.';
      if (net > gross) errors.net_payable = 'Net payable cannot exceed gross value.';
      if (paid > net) errors.paid_value = 'Paid value cannot exceed net payable.';
      if (catData.period_from && catData.period_to && new Date(catData.period_to) < new Date(catData.period_from)) {
        errors.period_to = 'Period To cannot be before Period From.';
      }
    }

    if (isBankGuaranteeCategory(newChain.category)) {
      const issue = catData.bg_issue_date;
      const expiry = catData.bg_expiry_date;
      const claimExpiry = catData.bg_claim_expiry_date;
      if (issue && expiry && new Date(expiry) <= new Date(issue)) {
        errors.bg_expiry_date = 'ValidUpto must be after issue date.';
      }
      if (expiry && claimExpiry && new Date(claimExpiry) < new Date(expiry)) {
        errors.bg_claim_expiry_date = 'Claim ValidUpto must be on/after ValidUpto.';
      }
    }

    if (matchesClearanceCategory(newChain.category)) {
      const submission = catData.submission_date;
      const target = catData.target_date;
      if (submission && target && new Date(target) < new Date(submission)) {
        errors.target_date = 'Target Date cannot be before Submission Date.';
      }
    }

    if (matchesMilestoneEotCategory(newChain.category)) {
      // Category fields are non-mandatory; only validate format when values are provided
      const eotDays = Number(catData.eot_days_requested);
      if (catData.eot_days_requested !== undefined && catData.eot_days_requested !== '' && !Number.isNaN(eotDays) && eotDays <= 0) {
        errors.eot_days_requested = 'EOT days requested must be greater than zero when provided.';
      }
      const costImpact = Number(catData.estimated_cost_impact);
      if (catData.financial_impact_applicable === 'Yes' && catData.estimated_cost_impact !== undefined && catData.estimated_cost_impact !== '' && !Number.isNaN(costImpact) && costImpact <= 0) {
        errors.estimated_cost_impact = 'Estimated Cost Impact must be greater than zero when provided.';
      }
    }

    if (newChain.category === 'Specialized Items') {
      // Lead Time is non-mandatory; only validate when a value is explicitly provided
      const leadDays = Number(catData.lead_time_days);
      if (catData.lead_time_days !== undefined && catData.lead_time_days !== '' && !Number.isNaN(leadDays) && leadDays <= 0) {
        errors.lead_time_days = 'Lead Time (Days) must be greater than zero when provided.';
      }
      if (catData.expected_delivery_date && newChain.letter_date && new Date(catData.expected_delivery_date) < new Date(newChain.letter_date)) {
        errors.expected_delivery_date = 'Expected Delivery Date cannot be before Letter Date.';
      }
    }

    if (matchesQualityCategory(newChain.category)) {
      const inspection = catData.inspection_date;
      const due = catData.retest_date;
      if (inspection && due && new Date(due) < new Date(inspection)) {
        errors.retest_date = 'Retest date cannot be before inspection date.';
      }
    }


    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCustomFields = () => {
    if (!formConfig.customFieldsEnabled) return null;
    for (const f of formConfig.customFields || []) {
      if (!f.required || f.hidden) continue;
      const v = customValues[f.fieldKey];
      if (v === undefined || v === null || v === '') {
        return `${f.label || f.fieldKey} is required.`;
      }
    }
    return null;
  };

  const handleCreateChain = async () => {
    if (!validateCreateChain()) return;
    const customFieldError = validateCustomFields();
    if (customFieldError) {
      notifyError(customFieldError);
      return;
    }
    if (!(await confirmDialog('Create this Correspondence Subject?', 'Create Correspondence Subject'))) return;
    if (useApiData) {
      const createViaApi = async () => {
        setIsCreating(true);
        try {
          const projectId = parseInt(newChain.project_id, 10);
          const entryDate = getToday();
          const letterDate = newChain.letter_date || entryDate;
          const dueDate = newChain.response_expected
            ? new Date(new Date(letterDate).getTime() + (Number(newChain.sla_days) || 0) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
            : null;

          const chainPayload = {
            project_id: projectId,
            subject: newChain.subject.trim(),
            category: newChain.category,
            location_block: newChain.location_block?.trim() || null,
            type: newChain.type,
            severity: newChain.severity,
            status: 'Open',
            targeted_reply_days: Number(newChain.sla_days) || 0,
            last_entry_date: letterDate,
          };

          const chainResp = await correspondenceAPI.createChain(chainPayload);
          const createdChain = normalizeApiChain(chainResp?.data || {});

          if (formConfig.customFieldsEnabled && createdChain?.id) {
            try {
              await adminAPI.saveCustomFieldValues('CorrespondenceChain', createdChain.id, {
                pageKey: 'correspondence_register',
                projectId,
                values: customValues,
                mode: 'create',
              });
            } catch (cvErr) {
              console.error('Custom field save failed:', cvErr);
              notifyError(cvErr?.response?.data?.message || 'Correspondence created but custom fields failed to save.');
            }
          }

          await correspondenceAPI.createEntry(createdChain.id, {
            ref_no: newChain.letter_ref_no.trim() || null,
            direction: newChain.direction,
            from: newChain.from.trim(),
            to: newChain.to.trim(),
            letter_date: letterDate,
            entry_date: entryDate,
            summary: newChain.summary.trim() || newChain.subject.trim(),
            response_expected: newChain.response_expected,
            sla_days: Number(newChain.sla_days) || 0,
            due_date: dueDate,
            mode: newChain.mode,
            attachments: newChain.attachments || [],
            priority: newChain.severity,
            severity: newChain.severity,
          });

          let linkedEntityType = null;
          let linkedEntityId = null;
          if (matchesRaBillCategory(newChain.category)) {
            const raResp = await raBillsAPI.create({
              projectId,
              billNo: String(newChain.category_data.ra_ref || `RA-${Date.now().toString().slice(-5)}`),
              status: String(newChain.category_data.ra_status || 'Submitted'),
              amount: Number(newChain.category_data.net_payable || 0),
              data: {
                ra_no: Number(newChain.category_data.ra_no || 0),
                period_from: newChain.category_data.period_from || null,
                period_to: newChain.category_data.period_to || null,
                gross_work_done: Number(newChain.category_data.gross_work_done || 0),
                deductions_total: Number(newChain.category_data.deductions_total || 0),
                net_payable: Number(newChain.category_data.net_payable || 0),
                payment_status: newChain.category_data.payment_status || 'Unpaid',
                payment_due_date: newChain.category_data.payment_due_date || null,
                linked_correspondence: createdChain.id,
              },
            });
            linkedEntityType = 'RA_BILL';
            linkedEntityId = raResp?.data?.id || null;
          } else if (isBankGuaranteeCategory(newChain.category)) {
            const bgResp = await bankGuaranteesAPI.create({
              projectId,
              bgNo: String(newChain.category_data.bg_number || `BG-${Date.now().toString().slice(-5)}`),
              status: String(newChain.category_data.bg_status || 'Active'),
              amount: Number(newChain.category_data.bg_amount || 0),
              data: {
                bg_type: String(newChain.category_data.bg_type || 'Security BG'),
                purpose: String(newChain.category_data.bg_purpose || ''),
                bank: String(newChain.category_data.bg_bank_name || ''),
                ifsc: String(newChain.category_data.bg_ifsc || ''),
                branch: String(newChain.category_data.bg_branch || ''),
                issue_date: newChain.category_data.bg_issue_date || null,
                expiry_date: newChain.category_data.bg_expiry_date || null,
                claim_expiry_date: newChain.category_data.bg_claim_expiry_date || null,
                linked_correspondence: createdChain.id,
              },
            });
            linkedEntityType = 'BANK_GUARANTEE';
            linkedEntityId = bgResp?.data?.id || null;
          } else if (matchesClearanceCategory(newChain.category)) {
            const project = projectOptions.find((p) => p.id === projectId);
            const clearanceResp = await clearancesAPI.create({
              projectId,
              title: String(newChain.category_data.clearance_type || newChain.subject || '').trim(),
              status: String(newChain.category_data.clearance_status || 'Submitted'),
              data: {
                clearance_ref: `${project?.agreement_prefix || 'GEN'}/CLR/${Date.now().toString().slice(-6)}`,
                clearance_type: String(newChain.category_data.clearance_type || '').trim(),
                authority: String(newChain.category_data.authority || '').trim(),
                submission_date: newChain.category_data.submission_date || null,
                target_date: newChain.category_data.target_date || null,
                remarks: String(newChain.category_data.clearance_remarks || newChain.summary || '').trim(),
                linked_correspondence: createdChain.id,
              },
            });
            linkedEntityType = 'CLEARANCE';
            linkedEntityId = clearanceResp?.data?.id || null;
          } else if (matchesMilestoneEotCategory(newChain.category)) {
            const milestoneResp = await milestonesAPI.create({
              projectId,
              title: String(newChain.category_data.request_no || newChain.subject || `EOT-${Date.now().toString().slice(-5)}`),
              status: String(newChain.category_data.shift_status || 'Submitted'),
              dueDate: newChain.category_data.requested_completion_date
                ? new Date(newChain.category_data.requested_completion_date).toISOString()
                : null,
              data: {
                request_no: String(newChain.category_data.request_no || ''),
                milestone_id: Number(newChain.category_data.milestone_id || 0),
                eot_days_requested: Number(newChain.category_data.eot_days_requested || 0),
                requested_completion_date: newChain.category_data.requested_completion_date || null,
                reason_category: String(newChain.category_data.reason_category || ''),
                financial_impact_applicable: String(newChain.category_data.financial_impact_applicable || 'No'),
                estimated_cost_impact: Number(newChain.category_data.estimated_cost_impact || 0),
                ld_risk_impact: Number(newChain.category_data.ld_risk_impact || 0),
                financial_remarks: String(newChain.category_data.financial_remarks || ''),
                remarks: String(newChain.category_data.eot_reason || ''),
                linked_correspondence: createdChain.id,
              },
            });
            linkedEntityType = 'MILESTONE';
            linkedEntityId = milestoneResp?.data?.id || null;
          } else if (newChain.category === 'Specialized Items') {
            const specializedResp = await deviationsAPI.create({
              projectId,
              title: String(newChain.category_data.item_name || newChain.subject || `SPC-${Date.now().toString().slice(-5)}`),
              category: 'Specialized Item',
              status: 'Submitted',
              data: {
                item_name: String(newChain.category_data.item_name || '').trim(),
                spec_ref: String(newChain.category_data.spec_ref || '').trim(),
                vendor_name: String(newChain.category_data.vendor_name || '').trim(),
                country_of_origin: String(newChain.category_data.country_of_origin || '').trim(),
                lead_time_days: Number(newChain.category_data.lead_time_days || 0),
                expected_delivery_date: newChain.category_data.expected_delivery_date || null,
                fat_required: String(newChain.category_data.fat_required || 'No'),
                warranty_months: Number(newChain.category_data.warranty_months || 0),
                special_instructions: String(newChain.category_data.special_instructions || ''),
                linked_correspondence: createdChain.id,
              },
            });
            linkedEntityType = 'SPECIALIZED_ITEM';
            linkedEntityId = specializedResp?.data?.id || null;
          } else if (matchesDeviationsExtraItemsCategory(newChain.category)) {
            const devResp = await deviationsAPI.create({
              projectId,
              title: String(newChain.category_data.deviation_description || newChain.category_data.extra_item_description || newChain.subject),
              category: 'Deviation',
              status: String(newChain.category_data.deviation_status || 'Submitted'),
              data: {
                deviation_no: String(newChain.category_data.deviation_no || `DEV-${Date.now().toString().slice(-5)}`),
                boq_item: String(newChain.category_data.boq_item_ref || ''),
                original_qty: Number(newChain.category_data.original_qty || 0),
                revised_qty: Number(newChain.category_data.revised_qty || newChain.category_data.extra_item_qty || 0),
                unit: String(newChain.category_data.unit || ''),
                original_rate: Number(newChain.category_data.original_rate || 0),
                revised_rate: Number(newChain.category_data.revised_rate || newChain.category_data.extra_item_rate || 0),
                linked_correspondence: createdChain.id,
                remarks: String(newChain.category_data.deviation_remarks || ''),
              },
            });
            linkedEntityType = 'DEVIATION';
            linkedEntityId = devResp?.data?.id || null;
          } else if (matchesScopeAgreementCategory(newChain.category)) {
            const scopeResp = await deviationsAPI.create({
              projectId,
              title: String(newChain.category_data.scope_summary || newChain.subject),
              category: 'Scope',
              status: 'Submitted',
              data: {
                change_order_ref: String(newChain.category_data.change_order_ref || ''),
                scope_summary: String(newChain.category_data.scope_summary || ''),
                scope_value_impact: Number(newChain.category_data.scope_value_impact || 0),
                approval_stage: String(newChain.category_data.approval_stage || ''),
                linked_correspondence: createdChain.id,
              },
            });
            linkedEntityType = 'SCOPE_CHANGE';
            linkedEntityId = scopeResp?.data?.id || null;
          } else if (newChain.category === 'Contractual / Claims') {
            const claimResp = await deviationsAPI.create({
              projectId,
              title: String(newChain.category_data.claim_ref || newChain.subject),
              category: 'Claim',
              status: 'Submitted',
              data: {
                claim_ref: String(newChain.category_data.claim_ref || ''),
                claim_amount: Number(newChain.category_data.claim_amount || 0),
                claim_basis: String(newChain.category_data.claim_basis || ''),
                claim_notice_date: newChain.category_data.claim_notice_date || null,
                linked_correspondence: createdChain.id,
              },
            });
            linkedEntityType = 'CLAIM';
            linkedEntityId = claimResp?.data?.id || null;
          } else if (matchesQualityCategory(newChain.category)) {
            const qaqcResp = await qaqcAPI.create({
              projectId,
              activity: String(newChain.subject || ''),
              result: String(newChain.category_data.qaqc_result || 'Conditional'),
              status: String(newChain.category_data.qaqc_status || 'Under Review'),
              data: {
                checklist_no: String(newChain.category_data.checklist_no || ''),
                category: String(newChain.category_data.qaqc_category || ''),
                inspection_date: newChain.category_data.inspection_date || null,
                inspector: String(newChain.category_data.inspector_name || ''),
                parameters_checked: String(newChain.category_data.parameters_checked || ''),
                defects_found: String(newChain.category_data.defects_found || ''),
                corrective_action: String(newChain.category_data.corrective_action || ''),
                retest_date: newChain.category_data.retest_date || null,
                remarks: String(newChain.category_data.qaqc_remarks || ''),
                linked_correspondence: createdChain.id,
              },
            });
            linkedEntityType = 'QAQC';
            linkedEntityId = qaqcResp?.data?.id || null;
          }

          if (linkedEntityType && linkedEntityId) {
            await correspondenceAPI.updateChain(createdChain.id, {
              linked_entity_type: linkedEntityType,
              linked_entity_id: linkedEntityId,
            });
          }

          await loadFromApi();
          setShowCreateModal(false);
          setShowSearchResults(false);
          setSearchMatches([]);
          setFormErrors({});
          setNewChain({
            project_id: '', subject: '', type: 'Query', category: 'General', location_block: '', severity: 'Routine',
            direction: 'Outward', letter_date: getToday(), letter_ref_no: '', from: DEC_INFRA, to: '', summary: '', sla_days: 7,
            response_expected: true, mode: 'Letter', category_data: {}, attachments: []
          });
          setCustomValues({});
          notifySuccess('Correspondence Subject created successfully.');
          onViewThread(createdChain.id);
        } catch (error) {
          console.error('Failed to create chain via API:', error);
          notifyError('Failed to create correspondence via API. Please retry.');
          await loadFromApi();
        } finally {
          setIsCreating(false);
        }
      };
      createViaApi();
      return;
    }

    notifyError('API required to create correspondence. Please ensure the API is available.');
  };

  const getProjectLabel = () => {
    if (projectFilter === 'all') return 'All Projects';
    return projectOptions.find((p) => p.id === parseInt(projectFilter, 10))?.name || 'Selected Project';
  };

  const handleDownloadFilteredPdf = async () => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      alert('From Date cannot be after To Date.');
      return;
    }

    const projectSelected = projectFilter !== 'all';
    const projectLabel = getProjectLabel();

    // For download, fetch chains+entries for this page so References column is accurate.
    // The list view API doesn't include all entries by default.
    let chainsForDownload = paginatedChains;
    let entriesForDownload = entriesSource;
    if (useApiData) {
      try {
        const { apiStatus, apiPriority } = resolveRegisterApiFilters(filter, statusFilter, priorityFilter, registerQuickFilter);
        const params = {
          page,
          pageSize: perPage,
          ...(projectFilter !== 'all' && { projectId: projectFilter }),
          ...(apiStatus !== 'all' && apiStatus && { status: apiStatus }),
          ...(categoryFilter !== 'all' && { category: categoryFilter }),
          ...(locationBlockFilter !== 'all' && { locationBlock: locationBlockFilter }),
          ...(debouncedLocationBlockSearch && { locationBlockSearch: debouncedLocationBlockSearch }),
          ...(apiPriority !== 'all' && apiPriority && { priority: apiPriority }),
          ...(debouncedDateFrom && { dateFrom: debouncedDateFrom }),
          ...(debouncedDateTo && { dateTo: debouncedDateTo }),
          ...(searchParam && { search: searchParam }),
          ...(registerQuickFilter === 'important' && { importantOnly: true }),
          ...(registerQuickFilter === 'pinned' && { pinnedOnly: true }),
          ...(registerQuickFilter === 'overdue' && { overdueOnly: true }),
          ...(registerQuickFilter === 'critical' && filter === 'all' && { criticalOnly: true }),
          ...(savedViewId && { savedViewId }),
          includeEntries: '1',
        };
        const resp = await correspondenceAPI.getChainsWithEntries(params);
        const data = resp?.data || {};
        chainsForDownload = safeArray(data.chains).map(normalizeApiChain);
        entriesForDownload = safeArray(data.entries);
      } catch (e) {
        console.warn('Download: could not fetch entries, falling back to current list:', e?.message || e);
      }
    }

    const buildStatsForDownload = () => {
      const stats = {};
      safeArray(entriesForDownload).forEach((entry) => {
        if (!entry) return;
        const chainId = entry.chain_id;
        if (!stats[chainId]) stats[chainId] = { count: 0, lastDate: '', latestLetterDate: null, latestLetterDirection: null };
        stats[chainId].count += 1;
        const anyDate = String(entry.letter_date || entry.entry_date || '');
        if (anyDate && (!stats[chainId].lastDate || anyDate > stats[chainId].lastDate)) {
          stats[chainId].lastDate = anyDate;
          stats[chainId].latestLetterDirection = entry.direction || null;
        }
      });
      return stats;
    };

    const statsForDownload = buildStatsForDownload();

    const getRefsForChain = (chainId) => {
      const refs = safeArray(entriesForDownload)
        .filter((e) => e && e.chain_id === chainId)
        .map((e) => e.ref_no || e.letter_ref_no)
        .filter(Boolean);
      return refs.join(', ');
    };

    const rows = chainsForDownload.map((chain, idx) => {
      const project = projectOptions.find((p) => p.id === chain.project_id);
      const stats = statsForDownload[chain.id] || {
        count: chain.entry_count || 0,
        lastDate: chain.last_entry_date || chain.created_at || '-',
        latestLetterDirection: null,
      };
      const serialNo = (page - 1) * perPage + idx + 1;
      const lastDate = stats.latestLetterDirection
        ? `${formatDateIN(stats.lastDate)} (${stats.latestLetterDirection})`
        : formatDateIN(stats.lastDate);
      const refs = getRefsForChain(chain.id) || '-';
      const core = [chain.subject, chain.category || 'General', chain.location_block || '—', chain.severity, chain.status, refs, stats.count || 0];
      return projectSelected ? [serialNo, lastDate, ...core] : [serialNo, project?.name || '-', lastDate, ...core];
    });

    const downloadHeaders = projectSelected
      ? ['Serial No', 'Last letter date', 'Correspondence Subject', 'Category', 'Location / block', 'Priority', 'Status', 'References', 'Entries']
      : ['Serial No', 'Project', 'Last letter date', 'Correspondence Subject', 'Category', 'Location / block', 'Priority', 'Status', 'References', 'Entries'];

    const title = `Correspondence Register - ${projectLabel}`;
    const subtitle = projectSelected
      ? `From: ${dateFrom ? formatDateIN(dateFrom) : 'Any'} | To: ${dateTo ? formatDateIN(dateTo) : 'Any'} | Page ${page}/${totalPages} (${paginatedChains.length} of ${totalCount} total)`
      : `Project: ${projectLabel} | From: ${dateFrom ? formatDateIN(dateFrom) : 'Any'} | To: ${dateTo ? formatDateIN(dateTo) : 'Any'} | Page ${page}/${totalPages} (${paginatedChains.length} of ${totalCount} total)`;

    const entriesColIndex = projectSelected ? 8 : 9;

    openPrintWindow({
      title,
      subtitle,
      bodyHtml: tableHtmlFromRows({
        headers: downloadHeaders,
        rows,
        emptyMessage: 'No correspondence found for selected filters.',
        amountColumnIndices: [entriesColIndex],
      }),
    });
  };

  const handleDownloadAllAttachments = async () => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      alert('From Date cannot be after To Date.');
      return;
    }

    setDownloadingAttachments(true);
    try {
      const ok = await downloadAttachmentsZip({
        chains: paginatedChains,
        entries: entriesSource,
        zipName: `correspondence_attachments_${getProjectLabel()}`,
      });
      if (!ok) alert('No attachments found for selected filters.');
    } finally {
      setDownloadingAttachments(false);
    }
  };

  return (
    <div>
      <div className="page-header-frozen">
        <div className="section-header">
          <h2>
            {titleMap[filter] || 'Correspondence Register'}
            <span className="count-badge">{totalCount}</span>
          </h2>
          <div className="correspondence-register-header-actions">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn-secondary" onClick={handleDownloadFilteredPdf}>
                Download PDF
              </button>
              <button className="btn-secondary" onClick={handleDownloadAllAttachments} disabled={downloadingAttachments}>
                {downloadingAttachments ? 'Preparing ZIP...' : 'Download Attachments'}
              </button>
              <button className="btn-primary" onClick={() => { setShowCreateModal(true); setShowSearchResults(false); setSearchMatches([]); setCustomValues(defaultsFromCustomFields()); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                New Correspondence Subject
              </button>
            </div>
          </div>
        </div>

        <div className="correspondence-search-hero">
          <div className="correspondence-search-hero-row">
            <div className="search-hero-inner">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="search"
                placeholder="Search by subject or reference number…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyRegisterSearch();
                  }
                }}
                aria-label="Search correspondence by subject or reference number"
              />
            </div>
            <button type="button" className="btn-primary correspondence-search-submit" onClick={applyRegisterSearch}>
              Search
            </button>
            {(searchQuery || searchParam) && (
              <button type="button" className="btn-secondary correspondence-search-clear" onClick={clearRegisterSearch}>
                Clear
              </button>
            )}
          </div>
          {searchParam && (
            <p className="correspondence-search-active" aria-live="polite">
              Showing results for &ldquo;{searchParam}&rdquo;
            </p>
          )}
        </div>
        <CorrespondenceViewsToolbar
          canManagePersonalFlags={canManagePersonalFlags}
          canManageSavedViews={canManageSavedViews}
          filter={filter}
          projects={projectOptions}
          quickFilter={registerQuickFilter}
          onQuickFilterChange={(v) => { setRegisterQuickFilter(v); setSavedViewId(null); setPage(1); apiCacheRef.current.clear(); }}
          savedViewId={savedViewId}
          onSavedViewChange={setSavedViewId}
          onApplySavedView={applySavedViewToRegister}
          getCurrentViewSnapshot={getCurrentViewSnapshot}
          onNavigateRegisterFilter={onNavigateRegisterFilter}
        />
        <div className="filter-bar correspondence-register-filters" style={{ marginBottom: 16 }}>
          <div className="filter-bar-row">
            <ProjectFilterSelect
              value={projectFilter}
              onChange={(value) => { setProjectFilter(value); setPage(1); }}
              projects={projectOptions}
            />
            <select
              value={dateRangePreset}
              onChange={(e) => {
                const val = e.target.value;
                setDateRangePreset(val);
                if (val === '') {
                  setDateFrom('');
                  setDateTo('');
                } else {
                  const days = parseInt(val, 10);
                  const to = new Date();
                  const from = new Date(to);
                  from.setDate(from.getDate() - (days - 1));
                  setDateFrom(from.toISOString().slice(0, 10));
                  setDateTo(to.toISOString().slice(0, 10));
                }
                setPage(1);
              }}
              title="Quick date range by last letter date"
            >
              <option value="">Date range</option>
              <option value="7">Last 7 days</option>
              <option value="15">Last 15 days</option>
              <option value="30">Last 30 days</option>
            </select>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateRangePreset(''); setDateFrom(e.target.value); }}
              title="Last letter date from"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateRangePreset(''); setDateTo(e.target.value); }}
              title="Last letter date to"
            />
            {filter === 'all' ? (
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="all">All Status</option>
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
              </select>
            ) : (
              <span className="filter-bar-spacer" aria-hidden="true" />
            )}
            <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="all">All Types</option>
              <option value="Information Only">Information Only</option>
              <option value="Issue">Issue</option>
              <option value="Query">Query</option>
              <option value="Drawings">Drawings</option>
            </select>
          </div>
          <div className="filter-bar-row">
            <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}>
              <option value="all">All Categories</option>
              {correspondenceCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <select
              value={locationBlockFilter}
              onChange={(e) => { setLocationBlockFilter(e.target.value); setPage(1); }}
              title="Filter by location / block"
            >
              <option value="all">All locations</option>
              <option value={LOCATION_BLOCK_UNSET}>Not set</option>
              {locationBlockOptions.map((loc) => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
            <input
              type="text"
              className="filter-location-text"
              value={locationBlockSearch}
              onChange={(e) => { setLocationBlockSearch(e.target.value); setPage(1); }}
              placeholder="Location text (partial match)…"
              title="Case-insensitive substring match. Not fuzzy (no typo tolerance)."
            />
            {filter !== 'critical' ? (
              <select value={priorityFilter} onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }}>
                <option value="all">All Priority</option>
                <option value="Immediate">Immediate</option>
                <option value="Urgent">Urgent</option>
                <option value="Routine">Routine</option>
                <option value="Critical">Critical</option>
              </select>
            ) : (
              <span className="filter-bar-spacer" aria-hidden="true" />
            )}
            <ResetFiltersButton onClick={resetRegisterFilters} />
          </div>
        </div>
      </div>


      <div className="table-container" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px 6px' }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>ABSTRACT OF ALL PROJECTS- CORRESPONDENCE</h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#334155' }}>As on: {projectSummaryAsOn ? formatDateIN(projectSummaryAsOn) : '-'}</span>
            <button
              type="button"
              className="btn-sm"
              onClick={() => setShowProjectSummary((v) => !v)}
              style={{
                minWidth: 92,
                background: '#7C3AED',
                border: '1px solid #7C3AED',
                color: '#fff',
                fontWeight: 600,
              }}
            >
              {showProjectSummary ? 'Collapse' : 'Expand'}
            </button>
          </div>
        </div>
        {showProjectSummary && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Sl.No.</th>
              <th>Name of Project</th>
              <th>Department</th>
              <th>Inward Letters</th>
              <th>Outward Letters</th>
              <th>Total Letters</th>
            </tr>
          </thead>
          <tbody>
            {projectSummaryLoading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>
                  Loading summary...
                </td>
              </tr>
            ) : projectSummaryRows.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>
                  No summary data.
                </td>
              </tr>
            ) : (
              <>
                {projectSummaryRows.map((row, idx) => (
                  <tr key={`proj-summary-${row.projectId || idx}`}>
                    <td>{idx + 1}</td>
                    <td>{row.projectName}</td>
                    <td>{row.departmentCode || '-'}</td>
                    <td>{Number(row.inwardLetters || 0)}</td>
                    <td>{Number(row.outwardLetters || 0)}</td>
                    <td style={{ fontWeight: 700 }}>{Number(row.totalLetters || 0)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="3" style={{ textAlign: 'right', fontWeight: 700 }}>Total</td>
                  <td style={{ fontWeight: 700 }}>{Number(projectSummaryTotals.inwardLetters || 0)}</td>
                  <td style={{ fontWeight: 700 }}>{Number(projectSummaryTotals.outwardLetters || 0)}</td>
                  <td style={{ fontWeight: 700 }}>{Number(projectSummaryTotals.totalLetters || 0)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <TablePagination
          className="pagination pagination--register-header"
          page={page}
          totalPages={totalPages}
          onPrevious={() => setPage(page - 1)}
          onNext={() => setPage(page + 1)}
        />
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {canManagePersonalFlags && <th className="bv-col-personal">Personal</th>}
              <th>Download</th>
              <th>Last letter date</th>
              <th>Letter Ref.</th>
              <th>Correspondence Subject</th>
              <th>Category</th>
              <th>Location / block</th>
              <th>Type</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Entries</th>
            </tr>
          </thead>
          <tbody>
            {isLoadingData ? (
              <tr>
                <td colSpan={canManagePersonalFlags ? 11 : 10} style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  Loading correspondence...
                </td>
              </tr>
            ) : paginatedChains.length === 0 ? (
              <tr>
                <td colSpan={canManagePersonalFlags ? 11 : 10} style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  No correspondence found matching the filters.
                </td>
              </tr>
            ) : (
              paginatedChains.map((chain) => {
                const project = projectOptions.find(p => p.id === chain.project_id);
                const chainAttachments = getChainAttachments(chain.id, entriesSource);
                const stats = chainStatsById[chain.id] || {
                  count: chain.entry_count || 0,
                  lastDate: chain.last_entry_date || chain.created_at || '-',
                  firstLetterRefDate: null,
                  latestLetterDate: chain.last_entry_date || null,
                  latestLetterDirection: chain.last_direction || null,
                };
                const refs = (chain.letter_refs || getChainEntries(chain.id, entriesSource)
                  .map((e) => e.ref_no || e.letter_ref_no)
                  .filter(Boolean)
                  .join(', ')) || '-';
                return (
                  <tr
                    key={chain.id}
                    className={`clickable ${chain.status === 'Closed' ? 'muted' : ''} ${chain.isPinnedForMe ? 'bv-row--pinned' : ''} ${chain.isImportantForMe ? 'bv-row--important' : ''}`}
                    onClick={() => onViewThread(chain.id)}
                  >
                    {canManagePersonalFlags && <td className="bv-col-personal" onClick={(e) => e.stopPropagation()}>
                      <div className="bv-personal-actions">
                        <button
                          type="button"
                          className={`bv-flag-btn bv-flag-btn--star ${chain.isImportantForMe ? 'is-active' : ''}`}
                          title={chain.isImportantForMe ? 'Remove from Important' : 'Mark as Important'}
                          aria-label={chain.isImportantForMe ? 'Remove from Important' : 'Mark as Important'}
                          onClick={(e) => handleToggleImportant(e, chain.id)}
                        >
                          <IconStar filled={chain.isImportantForMe} />
                        </button>
                        <button
                          type="button"
                          className={`bv-flag-btn bv-flag-btn--pin ${chain.isPinnedForMe ? 'is-active' : ''}`}
                          title={chain.isPinnedForMe ? 'Unpin correspondence' : 'Pin correspondence'}
                          aria-label={chain.isPinnedForMe ? 'Unpin correspondence' : 'Pin correspondence'}
                          onClick={(e) => handleTogglePin(e, chain.id)}
                        >
                          <IconPin />
                        </button>
                      </div>
                    </td>}
                    <td>
                      <button
                        className="btn-secondary btn-sm"
                        onClick={async (e) => {
                          e.stopPropagation();
                          const chainEntries = getChainEntries(chain.id, entriesSource);
                          const entries = chainEntries.length > 0
                            ? chainEntries
                            : safeArray((await correspondenceAPI.getEntries(chain.id))?.data);
                          setThreadReportPreview({ chain, project, entries });
                        }}
                      >
                        View
                      </button>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <span>{formatDateIN(stats.lastDate)}</span>
                        {stats.latestLetterDirection && (
                          <span
                            style={{
                              padding: '1px 7px',
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 700,
                              color: '#ffffff',
                              background: stats.latestLetterDirection === 'Inward' ? '#14b8a6' : '#f97316',
                              display: 'inline-block',
                              lineHeight: 1.2,
                            }}
                          >
                            {stats.latestLetterDirection}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word', fontSize: 12, color: '#374151' }}>{refs}</td>
                    <td
                      style={{ fontWeight: 500, maxWidth: 320, whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: 1.4 }}
                    >
                      <CorrespondenceSubjectTooltip projectName={project?.name} subject={chain.subject}>
                        {chain.subject}
                        {chainAttachments.length > 0 && (
                          <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280' }}>
                            ({chainAttachments.length} attachment{chainAttachments.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </CorrespondenceSubjectTooltip>
                    </td>
                    <td><span className="category-tag">{displayCorrespondenceCategory(chain.category || 'General')}</span></td>
                    <td style={{ fontSize: 12, color: '#374151', maxWidth: 120, whiteSpace: 'normal', wordBreak: 'break-word' }}>{chain.location_block || '—'}</td>
                    <td><StatusBadge tone={chain.type === 'Information Only' ? 'closed' : chain.type === 'Issue' ? 'high' : chain.type === 'Query' ? 'medium' : 'open'}>{chain.type}</StatusBadge></td>
                    <td><StatusBadge tone={getPriorityClass(chain.severity)}>{chain.severity}</StatusBadge></td>
                    <td><StatusBadge tone={chain.status.toLowerCase()}>{chain.status}</StatusBadge></td>
                    <td>{stats.count}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <TablePagination
          page={page}
          totalPages={totalPages}
          onPrevious={() => setPage(page - 1)}
          onNext={() => setPage(page + 1)}
        />
      </div>

      {threadReportPreview && (
        <ThreadReportPreviewModal
          chain={threadReportPreview.chain}
          project={threadReportPreview.project}
          entries={threadReportPreview.entries}
          onClose={() => setThreadReportPreview(null)}
        />
      )}

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Create New Correspondence Subject</h2>
              <button className="btn-close" onClick={() => setShowCreateModal(false)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <ConfiguredField fieldKey="project_id" formConfig={formConfig} defaultLabel="Project" defaultRequired>
                  <select value={newChain.project_id} onChange={(e) => setNewChain({ ...newChain, project_id: e.target.value })}>
                    <option value="">Select Project</option>
                    {projectOptions.filter(p => p.status === 'Active').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  {formErrors.project_id && <p style={{ color: '#dc2626', marginTop: 6, fontSize: 12 }}>{formErrors.project_id}</p>}
                </ConfiguredField>
                <ConfiguredField fieldKey="type" formConfig={formConfig} defaultLabel="Type">
                  <select value={newChain.type} onChange={(e) => setNewChain({ ...newChain, type: e.target.value })}>
                    <option value="Query">Query</option>
                    <option value="Issue">Issue / Problem</option>
                    <option value="Drawings">Drawings</option>
                    <option value="Information Only">Information Only</option>
                  </select>
                </ConfiguredField>
              </div>

              <ConfiguredField fieldKey="category" formConfig={formConfig} defaultLabel="Category (What is this correspondence about?)">
                <select value={newChain.category} onChange={(e) => handleCategoryChange(e.target.value)}>
                  {correspondenceCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </ConfiguredField>

              <ConfiguredField fieldKey="location_block" formConfig={formConfig} defaultLabel="Location / block">
                <input
                  type="text"
                  value={newChain.location_block || ''}
                  onChange={(e) => setNewChain({ ...newChain, location_block: e.target.value })}
                  placeholder="e.g. Block A, CH-01, Site office wing (optional)"
                />
              </ConfiguredField>

              {getCategoryFieldConfig(newChain.category).length > 0 && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <p style={{ margin: '0 0 10px', fontWeight: 600, fontSize: 13, color: '#374151' }}>
                    Category-specific details ({newChain.category})
                  </p>
                  <div className="form-row" style={{ flexWrap: 'wrap' }}>
                    {getCategoryFieldConfig(newChain.category).map(field => (
                      <div className="form-group" key={field.key} style={{ minWidth: 220, flex: '1 1 260px' }}>
                        <label>{field.label}{field.required && <span className="req"> *</span>}</label>
                        {field.type === 'select' ? (
                          <select
                            value={(newChain.category_data || {})[field.key] ?? ''}
                            onChange={(e) => handleCategoryFieldChange(field.key, e.target.value, 'text')}
                          >
                            <option value="">Select {field.label}</option>
                            {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        ) : field.type === 'textarea' ? (
                          <textarea
                            value={(newChain.category_data || {})[field.key] ?? ''}
                            onChange={(e) => handleCategoryFieldChange(field.key, e.target.value, 'text')}
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            rows={3}
                          />
                        ) : (
                          <input
                            type={field.type}
                            value={(newChain.category_data || {})[field.key] ?? ''}
                            onChange={(e) => handleCategoryFieldChange(field.key, e.target.value, field.type)}
                            placeholder={field.type === 'text' ? `Enter ${field.label.toLowerCase()}` : ''}
                          />
                        )}
                        {formErrors[field.key] && <p style={{ color: '#dc2626', marginTop: 6, fontSize: 12 }}>{formErrors[field.key]}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <ConfiguredField fieldKey="subject" formConfig={formConfig} defaultLabel="Subject" defaultRequired>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    value={newChain.subject}
                    onChange={(e) => setNewChain({ ...newChain, subject: e.target.value })}
                    placeholder="Enter subject..."
                    style={{ flex: 1 }}
                  />
                  <button className="btn-secondary btn-sm" onClick={handleSubjectSearch} type="button">Search</button>
                </div>
                {formErrors.subject && <p style={{ color: '#dc2626', marginTop: 6, fontSize: 12 }}>{formErrors.subject}</p>}
              </ConfiguredField>

              {showSearchResults && (
                <div style={{ background: '#F5F3FF', padding: 12, borderRadius: 6, marginBottom: 16, border: '1px solid #dbeafe' }}>
                  {searchMatches.length > 0 ? (
                    <>
                      <p style={{ fontSize: 13, fontWeight: 500, color: '#6D28D9', marginBottom: 8 }}>Possible matches found:</p>
                      {searchMatches.map(m => (
                        <div key={m.id} style={{ padding: '6px 0', borderBottom: '1px solid #dbeafe', cursor: 'pointer' }} onClick={() => { onViewThread(m.id); setShowCreateModal(false); }}>
                          <span style={{ fontSize: 13, color: '#1f2937' }}>{m.subject}</span>
                          <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 8 }}>({m.status})</span>
                        </div>
                      ))}
                      <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>Click a match to open, or continue to create new.</p>
                    </>
                  ) : (
                    <p style={{ fontSize: 13, color: '#6D28D9' }}>No existing matches found. You can create a new Correspondence Subject.</p>
                  )}
                </div>
              )}

              <div className="form-row">
                <ConfiguredField fieldKey="direction" formConfig={formConfig} defaultLabel="Direction">
                  <select
                    value={newChain.direction}
                    onChange={(e) => {
                      const dir = e.target.value;
                      setNewChain({
                        ...newChain,
                        direction: dir,
                        from: dir === 'Outward' ? DEC_INFRA : '',
                        to: dir === 'Inward' ? DEC_INFRA : '',
                      });
                    }}
                  >
                    <option value="Outward">Outward</option>
                    <option value="Inward">Inward</option>
                  </select>
                </ConfiguredField>
                <ConfiguredField fieldKey="letter_date" formConfig={formConfig} defaultLabel="Letter Date" defaultRequired>
                  <input
                    type="date"
                    value={newChain.letter_date}
                    onChange={(e) => setNewChain({ ...newChain, letter_date: e.target.value })}
                  />
                  {formErrors.letter_date && <p style={{ color: '#dc2626', marginTop: 6, fontSize: 12 }}>{formErrors.letter_date}</p>}
                </ConfiguredField>
                <ConfiguredField fieldKey="mode" formConfig={formConfig} defaultLabel="Mode">
                  <select
                    value={normalizeMode(newChain.mode)}
                    onChange={(e) => {
                      const mode = e.target.value;
                      setNewChain({ ...newChain, mode });
                      if (mode === 'Email') {
                        setFormErrors((prev) => {
                          const next = { ...prev };
                          delete next.letter_ref_no;
                          return next;
                        });
                      }
                    }}
                  >
                    <option value="Email">Email</option>
                    <option value="Letter">Letter</option>
                  </select>
                </ConfiguredField>
              </div>
              <div className="form-row">
                <ConfiguredField
                  fieldKey="letter_ref_no"
                  formConfig={formConfig}
                  defaultLabel="Letter Reference No"
                  defaultRequired={!isEmailCorrespondenceMode(newChain.mode)}
                >
                  <input
                    type="text"
                    value={newChain.letter_ref_no}
                    onChange={(e) => setNewChain({ ...newChain, letter_ref_no: e.target.value })}
                    placeholder={isEmailCorrespondenceMode(newChain.mode) ? 'Optional for email' : 'e.g. NH48/PH2/OUT/001'}
                  />
                  {formErrors.letter_ref_no && <p style={{ color: '#dc2626', marginTop: 6, fontSize: 12 }}>{formErrors.letter_ref_no}</p>}
                </ConfiguredField>
                <ConfiguredField fieldKey="severity" formConfig={formConfig} defaultLabel="Priority">
                  <select value={newChain.severity} onChange={(e) => setNewChain({ ...newChain, severity: e.target.value })}>
                    <option value="Immediate">Immediate</option>
                    <option value="Urgent">Urgent</option>
                    <option value="Routine">Routine</option>
                    <option value="Critical">Critical</option>
                  </select>
                </ConfiguredField>
              </div>

              <div className="form-row">
                <ConfiguredField fieldKey="from" formConfig={formConfig} defaultLabel="From" defaultRequired>
                  {briefDesignations.length > 0 && newChain.direction === 'Inward' ? (
                    <>
                      <select
                        value={briefDesignations.includes(newChain.from) ? newChain.from : '__other__'}
                        onChange={(e) => setNewChain({ ...newChain, from: e.target.value === '__other__' ? '' : e.target.value })}
                      >
                        {briefDesignations.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                        <option value="__other__">— Other (enter manually) —</option>
                      </select>
                      <input type="text" value={newChain.from} onChange={(e) => setNewChain({ ...newChain, from: e.target.value })} placeholder="Or type sender manually" style={{ marginTop: 6 }} />
                    </>
                  ) : briefDesignations.length > 0 && newChain.direction === 'Outward' ? (
                    <input type="text" value={DEC_INFRA} readOnly style={{ background: '#f3f4f6', cursor: 'default' }} title="Outward: From is always DEC Infra" />
                  ) : (
                    <input type="text" value={newChain.from} onChange={(e) => setNewChain({ ...newChain, from: e.target.value })} placeholder="Sender" />
                  )}
                  {formErrors.from && <p style={{ color: '#dc2626', marginTop: 6, fontSize: 12 }}>{formErrors.from}</p>}
                </ConfiguredField>
                <ConfiguredField fieldKey="to" formConfig={formConfig} defaultLabel="To" defaultRequired>
                  {briefDesignations.length > 0 && newChain.direction === 'Outward' ? (
                    <>
                      <select
                        value={briefDesignations.includes(newChain.to) ? newChain.to : '__other__'}
                        onChange={(e) => setNewChain({ ...newChain, to: e.target.value === '__other__' ? '' : e.target.value })}
                      >
                        {briefDesignations.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                        <option value="__other__">— Other (enter manually) —</option>
                      </select>
                      <input type="text" value={newChain.to} onChange={(e) => setNewChain({ ...newChain, to: e.target.value })} placeholder="Or type recipient manually" style={{ marginTop: 6 }} />
                    </>
                  ) : briefDesignations.length > 0 && newChain.direction === 'Inward' ? (
                    <input type="text" value={DEC_INFRA} readOnly style={{ background: '#f3f4f6', cursor: 'default' }} title="Inward: To is always DEC Infra" />
                  ) : (
                    <input type="text" value={newChain.to} onChange={(e) => setNewChain({ ...newChain, to: e.target.value })} placeholder="Recipient" />
                  )}
                  {formErrors.to && <p style={{ color: '#dc2626', marginTop: 6, fontSize: 12 }}>{formErrors.to}</p>}
                </ConfiguredField>
              </div>

              <div className="form-row">
                <ConfiguredField fieldKey="sla_days" formConfig={formConfig} defaultLabel="Targeted Reply (Days)">
                  <input type="number" value={newChain.sla_days} onChange={(e) => setNewChain({ ...newChain, sla_days: parseInt(e.target.value) || 0 })} />
                  {formErrors.sla_days && <p style={{ color: '#dc2626', marginTop: 6, fontSize: 12 }}>{formErrors.sla_days}</p>}
                </ConfiguredField>
              </div>

              <ConfiguredField fieldKey="summary" formConfig={formConfig} defaultLabel="Summary">
                <textarea value={newChain.summary} onChange={(e) => setNewChain({ ...newChain, summary: e.target.value })} placeholder="Brief description..." rows={3} />
              </ConfiguredField>

              <ConfiguredField fieldKey="attachments" formConfig={formConfig} defaultLabel="Attachments" defaultRequired>
                <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 6px' }}>Max {correspondenceMaxSizeLabel()} per file. Larger files are not allowed — please compress before uploading.</p>
                <input
                  type="file"
                  multiple
                  onChange={handleCreateAttachmentChange}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.msg,.eml"
                  disabled={attachmentUploading}
                />
                {attachmentUploading && (
                  <p style={{ fontSize: 12, color: '#b45309', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="spinner" style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #e5e7eb', borderTopColor: '#7C3AED', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Processing upload...
                  </p>
                )}
                {formErrors.attachments && <p style={{ color: '#dc2626', marginTop: 6, fontSize: 12 }}>{formErrors.attachments}</p>}
                {newChain.attachments.length > 0 && !attachmentUploading && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {newChain.attachments.map((file, idx) => (
                      <div key={`${file.name}-${file.size}-${idx}`} style={{ fontSize: 12, color: '#374151', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '6px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: '#16a34a' }} title="Upload complete">✓</span>
                          {file.name}
                        </span>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => handleRemoveCreateAttachment(idx)} style={{ flexShrink: 0 }} title="Remove attachment">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </ConfiguredField>

              {!formConfig.isHidden('response_expected') && (
                <div className="form-group">
                  <label>
                    <input type="checkbox" checked={newChain.response_expected} onChange={(e) => setNewChain({ ...newChain, response_expected: e.target.checked })} style={{ marginRight: 8 }} />
                    {formConfig.labelFor('response_expected', 'Response Expected')}
                  </label>
                </div>
              )}

              {formConfig.customFieldsEnabled && (
                <DynamicFieldsSection
                  fields={formConfig.customFields}
                  values={customValues}
                  onChange={onCustomChange}
                  title="Additional fields"
                />
              )}

              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowCreateModal(false)} disabled={isCreating}>Cancel</button>
                <button className="btn-primary" onClick={handleCreateChain} disabled={isCreating || attachmentUploading}>{isCreating ? 'Creating...' : 'Create Correspondence Subject'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorrespondenceRegister;
